/**
 * Preparar a imagem ANTES de subir: enquadrar, ampliar e inverter.
 *
 * O que ele resolve, na ordem em que doeu:
 *
 *  • PLANTA DE VERDADE VEM GRANDE E TORTA. Um scan A3 tem margem, carimbo,
 *    legenda e metade da folha em branco. Subir isso inteiro faz o desenho
 *    útil ocupar um terço da tela da parede, e não havia nada a fazer depois
 *    — a imagem era imutável.
 *
 *  • 16:9 POR PADRÃO, e isto não é gosto. É a proporção do documento de
 *    desenho, da tela de quem edita e da TV onde a planta vai parar. Imagem
 *    noutra proporção sobra numa direção e falta na outra, e quando ela é
 *    calco do desenho a sobra vira tarja. A proporção livre existe pra planta
 *    que vai viver sozinha, e aí o quadro é a própria imagem.
 *
 *  • INVERTER PORQUE PLANTA NASCE PRETO-NO-BRANCO. Numa TV de salão escuro,
 *    fundo branco é lanterna. Com a inversão, o mesmo scan vira traço claro
 *    sobre fundo escuro e combina com os temas escuros do desenho.
 *
 * O QUE SE VÊ É O QUE SOBE. O recorte é desenhado num canvas do tamanho do
 * quadro, e a exportação repete exatamente a mesma transformação numa escala
 * maior. Sem isso, "o que eu enquadrei" e "o que foi salvo" divergem — e a
 * pessoa só descobre depois, na parede.
 */

import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from "react";
import { Contrast, Maximize, Minus, Plus, RotateCcw } from "lucide-react";
import clsx from "clsx";

import {
  encaixeDeConter, fatorDeExportacao, zoomAncorado,
} from "@/lib/enquadramento";

import { Button } from "./Button";
import { Modal } from "./Modal";

/** Proporção do documento de desenho. Ver o cabeçalho. */
const PROPORCAO = 16 / 9;

/**
 * Maior lado da imagem exportada.
 *
 * Acima disto não se ganha nada: a planta é desenho de linha vista de longe,
 * e o teto de 8 MB do upload chega antes da nitidez. Abaixo, aparece serrilha
 * no zoom da tela da unidade.
 */
const LADO_MAX_EXPORTADO = 2400;

/** Acima disto, PNG vira JPEG — PNG de scan fotográfico estoura o limite. */
const LIMITE_PNG = 4 * 1024 * 1024;

interface Props {
  arquivo: File;
  enviando: boolean;
  aoCancelar: () => void;
  aoConfirmar: (imagem: File) => void;
}

export function PreparadorDeImagem({
  arquivo, enviando, aoCancelar, aoConfirmar,
}: Props) {
  const quadroRef = useRef<HTMLDivElement>(null);
  const telaRef = useRef<HTMLCanvasElement>(null);
  const bitmapRef = useRef<ImageBitmap | HTMLImageElement | null>(null);

  const [pronta, setPronta] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [livre, setLivre] = useState(false);
  const [inverter, setInverter] = useState(false);
  const [escala, setEscala] = useState(1);
  const [desloc, setDesloc] = useState({ x: 0, y: 0 });
  const [quadro, setQuadro] = useState({ largura: 0, altura: 0 });
  const arrasto = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null);

  const dimensoes = useCallback(() => {
    const b = bitmapRef.current;
    if (!b) return { largura: 0, altura: 0 };
    return "naturalWidth" in b
      ? { largura: b.naturalWidth, altura: b.naturalHeight }
      : { largura: b.width, altura: b.height };
  }, []);

  /** Enquadra a imagem inteira dentro do quadro. */
  const ajustar = useCallback(() => {
    const encaixe = encaixeDeConter(dimensoes(), quadro);
    setEscala(encaixe.escala);
    setDesloc(encaixe.desloc);
  }, [dimensoes, quadro]);

  // `createImageBitmap` em vez de object URL: bitmap não tem tempo de vida
  // pra alguém revogar na hora errada — a lição que a planta já deu uma vez.
  useEffect(() => {
    let vivo = true;
    let criado: ImageBitmap | null = null;
    (async () => {
      try {
        if (typeof createImageBitmap === "function") {
          criado = await createImageBitmap(arquivo);
          if (!vivo) { criado.close(); return; }
          bitmapRef.current = criado;
        } else {
          const img = new Image();
          const url = URL.createObjectURL(arquivo);
          await new Promise<void>((ok, falhou) => {
            img.onload = () => ok();
            img.onerror = () => falhou(new Error("não abriu"));
            img.src = url;
          });
          URL.revokeObjectURL(url);
          if (!vivo) return;
          bitmapRef.current = img;
        }
        setPronta(true);
      } catch {
        if (vivo) setErro("Não consegui abrir este arquivo. Use PNG, JPG ou WebP.");
      }
    })();
    return () => {
      vivo = false;
      criado?.close();
      bitmapRef.current = null;
    };
  }, [arquivo]);

  // O quadro é medido, não calculado: é ele que define a resolução do
  // recorte, e chutar a largura erraria o enquadramento em telas estreitas.
  useLayoutEffect(() => {
    const no = quadroRef.current;
    if (!no || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(([e]) => {
      const largura = e.contentRect.width;
      setQuadro({ largura, altura: livre ? e.contentRect.height : largura / PROPORCAO });
    });
    obs.observe(no);
    return () => obs.disconnect();
  }, [livre]);

  useEffect(() => { if (pronta) ajustar(); }, [pronta, ajustar, livre]);

  /** Desenha o estado atual numa área de `fator` vezes o quadro. */
  const pintar = useCallback((ctx: CanvasRenderingContext2D, fator: number) => {
    const b = bitmapRef.current;
    if (!b) return;
    ctx.save();
    // Fundo: branco, ou preto quando invertido. Scan com transparência
    // sairia com buraco preto no PDF sem isto.
    ctx.fillStyle = inverter ? "#000000" : "#ffffff";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.filter = inverter ? "invert(1)" : "none";
    ctx.setTransform(escala * fator, 0, 0, escala * fator,
                     desloc.x * fator, desloc.y * fator);
    ctx.drawImage(b as CanvasImageSource, 0, 0);
    ctx.restore();
  }, [escala, desloc, inverter]);

  useEffect(() => {
    const tela = telaRef.current;
    const ctx = tela?.getContext("2d");
    if (!tela || !ctx || !pronta || !quadro.largura) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    tela.width = Math.round(quadro.largura * dpr);
    tela.height = Math.round(quadro.altura * dpr);
    pintar(ctx, dpr);
  }, [pronta, quadro, pintar]);

  function ampliar(fator: number, centro?: { x: number; y: number }) {
    const ancora = centro ?? { x: quadro.largura / 2, y: quadro.altura / 2 };
    // Ancorado: o pixel sob o cursor continua sob o cursor. Ver
    // `zoomAncorado` — sem isso, ampliar um detalhe vira tentativa e erro.
    const novo = zoomAncorado({ escala, desloc }, fator, ancora);
    setEscala(novo.escala);
    setDesloc(novo.desloc);
  }

  async function confirmar() {
    const { largura } = dimensoes();
    if (!largura || !quadro.largura) return;
    const fator = fatorDeExportacao(quadro, largura, escala, LADO_MAX_EXPORTADO);
    const saida = document.createElement("canvas");
    saida.width = Math.max(1, Math.round(quadro.largura * fator));
    saida.height = Math.max(1, Math.round(quadro.altura * fator));
    const ctx = saida.getContext("2d");
    if (!ctx) { setErro("Este navegador não conseguiu preparar a imagem."); return; }
    pintar(ctx, fator);

    const png = await new Promise<Blob | null>((r) => saida.toBlob(r, "image/png"));
    let blob = png;
    let nome = "planta.png";
    if (!blob || blob.size > LIMITE_PNG) {
      const jpg = await new Promise<Blob | null>(
        (r) => saida.toBlob(r, "image/jpeg", 0.92));
      if (jpg) { blob = jpg; nome = "planta.jpg"; }
    }
    if (!blob) { setErro("Não consegui gerar a imagem recortada."); return; }
    aoConfirmar(new File([blob], nome, { type: blob.type }));
  }

  return (
    <Modal
      open
      onClose={aoCancelar}
      size="lg"
      title="Preparar a planta"
      description="Enquadre a parte útil do desenho. O que estiver no quadro é o que será salvo."
      footer={
        <>
          <Button variant="secondary" onClick={aoCancelar} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} loading={enviando} disabled={!pronta}>
            Importar
          </Button>
        </>
      }
    >
      {erro ? (
        <p className="text-sm text-rose-600 dark:text-rose-400">{erro}</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => ampliar(1 / 1.25)}
                    title="Diminuir">
              <Minus className="h-4 w-4" aria-hidden />
            </Button>
            <Button variant="secondary" onClick={() => ampliar(1.25)} title="Ampliar">
              <Plus className="h-4 w-4" aria-hidden />
            </Button>
            <Button variant="secondary" onClick={ajustar} title="Enquadrar tudo">
              <Maximize className="mr-1.5 h-4 w-4" aria-hidden />
              Ajustar
            </Button>
            <Button
              variant={inverter ? "primary" : "secondary"}
              onClick={() => setInverter((v) => !v)}
              title="Inverter claro e escuro"
            >
              <Contrast className="mr-1.5 h-4 w-4" aria-hidden />
              Inverter
            </Button>
            <button
              type="button"
              onClick={() => setLivre((v) => !v)}
              aria-pressed={!livre}
              className={clsx(
                "ml-auto rounded-md border px-2.5 py-1.5 text-sm font-medium",
                livre
                  ? "border-surface-300 text-slate-500 dark:border-slate-700 dark:text-slate-400"
                  : "border-brand-500 text-brand-700 dark:text-brand-300",
              )}
            >
              16:9
            </button>
          </div>

          <div
            ref={quadroRef}
            className="relative overflow-hidden rounded-lg border border-surface-300 bg-surface-100 dark:border-slate-700 dark:bg-slate-800"
            style={livre ? { height: 360 } : { aspectRatio: `${16} / ${9}` }}
          >
            <canvas
              ref={telaRef}
              className="h-full w-full cursor-grab touch-none active:cursor-grabbing"
              style={{ width: quadro.largura, height: quadro.altura }}
              onPointerDown={(e) => {
                (e.currentTarget as Element).setPointerCapture(e.pointerId);
                arrasto.current = {
                  x: e.clientX, y: e.clientY, dx: desloc.x, dy: desloc.y,
                };
              }}
              onPointerMove={(e) => {
                const a = arrasto.current;
                if (!a) return;
                setDesloc({
                  x: a.dx + (e.clientX - a.x),
                  y: a.dy + (e.clientY - a.y),
                });
              }}
              onPointerUp={() => { arrasto.current = null; }}
              onPointerCancel={() => { arrasto.current = null; }}
              onWheel={(e) => {
                const caixa = e.currentTarget.getBoundingClientRect();
                ampliar(e.deltaY < 0 ? 1.12 : 1 / 1.12, {
                  x: e.clientX - caixa.left, y: e.clientY - caixa.top,
                });
              }}
            />
            {!pronta && (
              <div className="absolute inset-0 grid place-items-center text-sm text-slate-500">
                Abrindo a imagem...
              </div>
            )}
          </div>

          <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <RotateCcw className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Arraste para mover, role para ampliar. O quadro 16:9 é a proporção
            do desenho e da TV — em proporção livre, a imagem vira o quadro.
          </p>
        </div>
      )}
    </Modal>
  );
}
